import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import React, { useEffect, useRef } from "react";
import { Spinner } from "../components";
import AuthRecovery from "./AuthRecovery";
import MsalRequestInterceptor from "./MsalRequestInterceptor";
import { getTokenBroker, useSessionState } from "./session";
import { resolveAccount, startSessionRefreshTriggers } from "./token-broker";

type MsalAuthProviderProps = {
  children: React.ReactNode;
};

const MsalAuthProvider = ({ children }: MsalAuthProviderProps) => {
  const { instance, accounts, inProgress } = useMsal();
  const broker = getTokenBroker(instance);
  const session = useSessionState(broker);
  const signInStarted = useRef(false);
  const hasAccount = accounts.length > 0;

  useEffect(() => {
    if (hasAccount) {
      signInStarted.current = false;
      resolveAccount(instance);
      return;
    }
    if (inProgress !== InteractionStatus.None || signInStarted.current) return;

    signInStarted.current = true;
    void broker.signIn();
  }, [broker, hasAccount, inProgress, instance]);

  useEffect(() => startSessionRefreshTriggers(broker), [broker]);

  if (!hasAccount) {
    if (session.status === "signin-required")
      return <AuthRecovery broker={broker} session={session} variant="page" />;

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100vw",
          height: "100vh",
        }}
        role="status"
        aria-label="Signing in"
      >
        <Spinner />
      </div>
    );
  }

  return (
    <>
      {session.status === "signin-required" && (
        <AuthRecovery broker={broker} session={session} variant="banner" />
      )}
      <MsalRequestInterceptor>{children}</MsalRequestInterceptor>
    </>
  );
};

export default MsalAuthProvider;
